import axios from 'axios';
import {
  confirmPasswordReset as fbConfirmPasswordReset,
  createUserWithEmailAndPassword as createUserWithFirebase,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  getAuth as getFbAuth,
  linkWithCredential,
  onAuthStateChanged,
  signInAnonymously as fbSignInAnonymously,
  signInWithEmailAndPassword,
  User,
  UserCredential,
  verifyPasswordResetCode as fbVerifyPasswordResetCode,
} from 'firebase/auth';

import {
  MigrateAnonymouseUserToAuthenticatedUserDocument,
  MigrateAnonymouseUserToAuthenticatedUserPayload,
} from '~/generated/graphql/cocolocala-bff';
import { fetchGraphQL } from '~/repository/graphql/cocolocala-bff';
import { cocolocalaSignGrpcRequest } from '~/repository/userService';
import { logger } from '~/utils/loggerUtil';

import { firebaseApp } from './initialize';

export const getAuth = () => getFbAuth(firebaseApp);

export const currentUser = getAuth().currentUser;

export const createUserWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  const auth = getAuth();
  if (auth.currentUser && auth.currentUser.isAnonymous) {
    logger.debug('Firebase Create With Anonymouse', email);
    // 匿名ユーザだったとき
    const credential = EmailAuthProvider.credential(email, password);

    return await linkWithCredential(auth.currentUser, credential);
  } else {
    logger.debug('Firebase Create', email);
    return createUserWithFirebase(auth, email, password);
  }
};

export const onAuthStateChangedCallback = (
  callback: (user: User | null) => void
) => onAuthStateChanged(getAuth(), user => callback(user));

/**
 * Firebase AuthenticationがInitializeされた後、一度だけ呼ばれる。Init済みの際に呼ばれると、1度だけ即時実行する
 */
export const onAuthInit = async (): Promise<User | null> => {
  return new Promise(resolve => {
    const unsubscribe = getAuth().onAuthStateChanged(user => {
      unsubscribe();
      resolve(user);
    });
  });
};

export const userStatus = user => {
  if (!user) {
    return 'not-sign-in';
  } else if (user.email && user.emailVerified) {
    return 'sign-in-email-verified';
  } else if (user.isAnonymous) {
    return 'sign-in-anonymous';
  } else {
    return 'sign-in-else';
  }
};

export const signInToAnonymous = async () => {
  const result = await fbSignInAnonymously(getAuth());
  logger.debug('anonymous sign in success!');
  logger.debug(result.user);
  return result;
};

export const signUpWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<UserCredential | undefined> => {
  const auth = getAuth();
  // NOTE: (yoshimitsu) currentUserは参照のため認証後のユーザーを取得しないようコピーを作成しておく
  const prevUser = auth.currentUser !== null ? { ...auth.currentUser } : null;
  let userCredential: UserCredential | undefined = undefined;

  try {
    userCredential = await createUserWithEmailAndPassword(email, password);
    const newUser = userCredential.user;

    // 匿名ユーザのFirestoreのデータを引き継ぐ
    if (prevUser !== null && newUser !== null && prevUser.isAnonymous) {
      const idToken = await newUser.getIdToken();
      await fetchGraphQL<MigrateAnonymouseUserToAuthenticatedUserPayload>(
        MigrateAnonymouseUserToAuthenticatedUserDocument,
        {
          input: {
            anonymouseUid: prevUser.uid,
            authenticatedUid: newUser.uid,
          },
        },
        { authorization: `Bearer ${idToken}` }
      );
    }

    const idToken = await newUser.getIdToken();
    await cocolocalaSignGrpcRequest(idToken, email);
  } catch (error) {
    logger.exceptionToSentry(new Error(`sign up error: ${error}`));

    try {
      if (typeof userCredential !== 'undefined') {
        userCredential = await rollbackUser(userCredential);
      }
    } catch (rollbackError) {
      logger.exceptionToSentry(
        new Error(`sign up rollback error: ${rollbackError}`)
      );
      throw rollbackError;
    }
  }

  if (
    typeof userCredential?.user !== 'undefined' &&
    !userCredential.user.isAnonymous
  ) {
    const idToken = await userCredential.user.getIdToken();
    await addSingInSession(idToken);
  }

  return userCredential;
};

/**
 * FirebaseAuthにはサインアップできたけど、UserServiceには通信失敗したときは一度サインアップしたユーザを取り消して
 * 匿名ユーザにログイン、旅行プランを再セットするようなロールバック処理をする
 * @param userCredential
 */
const rollbackUser = async (
  userCredential: UserCredential
): Promise<UserCredential> => {
  await deleteUser(userCredential.user);
  const anonymousUserCredential = await signInToAnonymous();
  const newUser = anonymousUserCredential.user;

  if (typeof newUser !== 'undefined') {
    const idToken = await newUser.getIdToken();
    // NOTE: (yoshimitsu) 匿名ユーザーから会員登録済みユーザーへの移行するクエリだが、
    // ロールバックするために通常時とは逆の処理を行うので、ロールバックもとのユーザーをanonymousユーザー、新規に作成したanonymousユーザーをauthenticatedユーザーとして扱う
    await fetchGraphQL<MigrateAnonymouseUserToAuthenticatedUserPayload>(
      MigrateAnonymouseUserToAuthenticatedUserDocument,
      {
        input: {
          anonymouseUid: userCredential.user.uid,
          authenticatedUid: newUser.uid,
        },
      },
      { authorization: `Bearer ${idToken}` }
    );
  }

  return anonymousUserCredential;
};

/**
 * Fails with an error if the email address and password do not match.
 * @param email
 * @param password
 */
export const signInWithEmailUser = async (email: string, password: string) => {
  const auth = getAuth();
  const prevUser = auth.currentUser;

  return signInWithEmailAndPassword(auth, email, password)
    .then(userCredential => {
      // 匿名ユーザのFirestoreのデータを引き継ぐ
      if (
        prevUser !== null &&
        userCredential.user !== null &&
        prevUser.isAnonymous
      ) {
        const newUser = userCredential.user;
        return newUser
          .getIdToken()
          .then(idToken => {
            return fetchGraphQL<
              MigrateAnonymouseUserToAuthenticatedUserPayload
            >(
              MigrateAnonymouseUserToAuthenticatedUserDocument,
              {
                input: {
                  anonymouseUid: prevUser.uid,
                  authenticatedUid: newUser.uid,
                },
              },
              { authorization: `Bearer ${idToken}` }
            );
          })
          .then(() => userCredential);
      } else {
        return userCredential;
      }
    })
    .catch(error => {
      // FirebaseAuthにはサインアップできたけど、UserServiceには通信失敗したときは一度サインアップしたユーザを取り消して
      // 匿名ユーザにログイン、旅行プランを再セットするようなロールバック処理をする
      if (error && error.userCredential && error.userCredential.user) {
        const userCredential = error.userCredential;
        return deleteUser(userCredential.user!)
          .then(() => {
            return signInToAnonymous();
          })
          .then(anonymoseUserCredential => {
            const newUser = anonymoseUserCredential.user;
            return newUser
              ?.getIdToken()
              .then(idToken => {
                return fetchGraphQL<
                  MigrateAnonymouseUserToAuthenticatedUserPayload
                >(
                  MigrateAnonymouseUserToAuthenticatedUserDocument,
                  {
                    input: {
                      anonymouseUid: userCredential.uid,
                      authenticatedUid: newUser.uid,
                    },
                  },
                  { authorization: `Bearer ${idToken}` }
                );
              })
              .then(() => userCredential);
          });
      } else {
        return Promise.reject(error);
      }
    })
    .then(userCredential => {
      if (
        userCredential &&
        userCredential.user &&
        !userCredential.user.isAnonymous
      ) {
        return userCredential.user
          .getIdToken()
          .then(addSingInSession)
          .then(() => userCredential);
      } else {
        return userCredential;
      }
    });
};

export const addSingInSession = (idToken: string) => {
  return axios.get('/_co/s/api/auth/login', {
    params: {
      idToken,
    },
  });
};

export const verifyPasswordResetCode = (
  actionCode: string
): Promise<string> => {
  getAuth();
  return fbVerifyPasswordResetCode(getAuth(), actionCode);
};

export const confirmPasswordReset = (
  actionCode: string,
  newPassword: string
): Promise<void> => {
  return fbConfirmPasswordReset(getAuth(), actionCode, newPassword);
};

export const deleteUser = (user: User): Promise<void> => user.delete();

/**
 * Emailアドレスが既に登録されているか
 * @param email
 */
export const isExistsEmail = (email: string) => {
  return fetchSignInMethodsForEmail(getAuth(), email).then(
    providers =>
      providers.findIndex(
        p => p === EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD
      ) !== -1
  );
};

export const signOut = (): Promise<void> => getAuth().signOut();

export const deleteSignInSession = () => {
  return axios.get('/_co/s/api/auth/logout');
};

/**
 * 匿名ユーザを認証ユーザへコンバートする
 * @param currentUser
 * @param email
 * @param password
 * https://firebase.google.com/docs/auth/web/anonymous-auth?hl=ja#convert-an-anonymous-account-to-a-permanent-account
 */
export const convertAnonymousToPermanent = async (
  currentUser: User,
  email: string,
  password: string
): Promise<User | null> => {
  const credential = EmailAuthProvider.credential(email, password);
  return (await linkWithCredential(currentUser, credential)).user;
};
